import React, { useEffect, useState } from 'react';

import { useViewportContext } from 'src/components/viewport/context';
import { PriceRowContextContainer } from '../price-row-context';
import { useProductContext } from '../../product-context';

import { insertTableAds } from '../ads';
import { OptionTypeEnum } from 'src/components/filter-dropdown';
import { getOffersWithUnfeaturedPlaceholder } from '../unfeatured-offers-placeholder';
import PriceListFooter from './price-list-footer';

import {
  createOfferFromPriceItem,
  getUnfeaturedPlaceholderData,
} from '../utils';

import { PriceRow } from '../price-row/row-types';
import type { IPriceListBodyProps } from './types';

import { PriceList } from './styles';
import { ViewSize } from '../price-row/types';

export const PriceListBody: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<IPriceListBodyProps>>
> = props => {
  const {
    sortedPrices,
    numberOfVisiblePrices,
    campaign,
    isPrimaryList,
    currentSorting = OptionTypeEnum.CHEAPEST_PRICE,
    showProductInformationTexts,
    ourChoiceStoreId,
  } = props;
  const { product } = useProductContext();
  const { isTablet, isDesktop } = useViewportContext();

  const unfeaturedPlaceholderData = getUnfeaturedPlaceholderData(sortedPrices);

  const [showUnfeaturedPlaceholder, setShowUnfeaturedPlaceholder] = useState(
    Boolean(unfeaturedPlaceholderData)
  );
  const [showMorePrices, setShowMorePrices] = useState(false);

  const filteredPrices = sortedPrices.filter(item => {
    // Filter to only show featured offers if placeholder should be shown
    return unfeaturedPlaceholderData && showUnfeaturedPlaceholder
      ? item.store?.featured && item.externalUri
      : item;
  });

  const insertUnfeaturedOffersPlaceholder = (offerRows: JSX.Element[]) =>
    unfeaturedPlaceholderData && showUnfeaturedPlaceholder
      ? getOffersWithUnfeaturedPlaceholder(
          offerRows,
          setShowUnfeaturedPlaceholder,
          unfeaturedPlaceholderData
        )
      : offerRows;

  const getViewSize = (): ViewSize => {
    if (isDesktop) return ViewSize.LARGE;
    if (isTablet) return ViewSize.MEDIUM;
    return ViewSize.SMALL;
  };

  // The only reason we use this mounted variable
  // is to delay toggling of async prop on ad container
  // until after initial mount. Otherwise, all ads break.
  // Hopefully something to be removed with a rewrite of the ads.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <>
      <PriceList>
        {sortedPrices &&
          sortedPrices.length > 0 &&
          insertTableAds(
            insertUnfeaturedOffersPlaceholder(
              filteredPrices
                .slice(0, showMorePrices ? undefined : numberOfVisiblePrices)
                .map((item, index) => {
                  const rowKey = `${item.id}-${index}`;
                  const offer = createOfferFromPriceItem(item);

                  return (
                    <PriceRowContextContainer
                      key={rowKey}
                      campaign={campaign}
                      offer={offer}
                      product={product}
                      isFeatured={Boolean(offer.featured && offer.externalUri)}
                      sorting={currentSorting}
                      setShowUnfeaturedPlaceholder={() =>
                        setShowUnfeaturedPlaceholder(true)
                      }
                      showUnfeaturedHideButton={Boolean(
                        unfeaturedPlaceholderData
                      )}
                      viewSize={getViewSize()}
                      isOurChoice={offer.store?.id === ourChoiceStoreId}
                      isInPriceList
                    >
                      <PriceRow
                        item={item}
                        index={index}
                        isInPrimaryList={isPrimaryList}
                      />
                    </PriceRowContextContainer>
                  );
                })
            ),
            mounted
          ).map(row => <li key={row.key}>{row}</li>)}
      </PriceList>
      <PriceListFooter
        showProductInformationTexts={showProductInformationTexts}
        offersCount={filteredPrices.length}
        showMorePrices={showMorePrices}
        numberOfVisiblePrices={numberOfVisiblePrices}
        setShowMorePrices={setShowMorePrices}
      />
    </>
  );
};
